/**
 * ActivityTile - Steps and workout summary
 */

import React from 'react';
import { FireIcon } from '@heroicons/react/24/outline';
import { Panel, Spinner } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY } from '../../design/tokens';

interface ActivityTileProps {
  steps: number | null;
  stepsTarget?: number;
  workoutMinutes: number | null;
  trainingMinutesWeek: number | null;
  loading?: boolean;
}

const ActivityTile: React.FC<ActivityTileProps> = ({
  steps,
  stepsTarget = 10000,
  workoutMinutes,
  trainingMinutesWeek,
  loading = false,
}) => {
  if (loading) {
    return (
      <Panel style={{ minHeight: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="md" />
      </Panel>
    );
  }

  const hasData = steps !== null || workoutMinutes !== null;
  const stepsProgress = steps !== null ? Math.min((steps / stepsTarget) * 100, 100) : 0;

  return (
    <Panel>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8], marginBottom: SPACING[12] }}>
        <FireIcon style={{ width: '18px', height: '18px', color: COLORS.status.warning }} />
        <span
          style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h3,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Activity
        </span>
      </div>

      {!hasData ? (
        <div style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.body.sizes.default, textAlign: 'center', padding: SPACING[8] }}>
          No activity data
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
          {steps !== null && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACING[4] }}>
                <span style={{ fontSize: TYPOGRAPHY.body.sizes.default, fontWeight: '600', color: COLORS.text.primary }}>
                  {steps.toLocaleString()} steps
                </span>
                <span style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary }}>
                  {Math.round(stepsProgress)}%
                </span>
              </div>
              <div style={{ height: '6px', backgroundColor: COLORS.bg.hover, borderRadius: '3px', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${stepsProgress}%`,
                    height: '100%',
                    backgroundColor: stepsProgress >= 100 ? COLORS.status.success : COLORS.accent.default,
                    borderRadius: '3px',
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: SPACING[16] }}>
            {workoutMinutes !== null && workoutMinutes > 0 && (
              <div>
                <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary }}>Workout</div>
                <span style={{ fontSize: TYPOGRAPHY.body.sizes.default, fontWeight: '600', color: COLORS.text.primary }}>
                  {workoutMinutes}m
                </span>
              </div>
            )}
            {trainingMinutesWeek !== null && (
              <div>
                <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary }}>Week</div>
                <span style={{ fontSize: TYPOGRAPHY.body.sizes.default, fontWeight: '600', color: COLORS.text.primary }}>
                  {Math.round(trainingMinutesWeek / 60)}h
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
};

export default ActivityTile;
